import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { History, Filter, RefreshCw, FileText, CheckSquare, ArrowRight, Wrench, Boxes, Workflow } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    entity_type: '',
    start_date: '',
    end_date: '',
    limit: 100
  });

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      params.append('limit', filters.limit.toString());

      const [logsRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/audit-logs?${params}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/audit-logs/summary?days=30`, { headers: getAuthHeader() })
      ]);

      setLogs(logsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Only super admins can view audit logs.');
      } else {
        toast.error('Failed to fetch audit logs');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const formatTimestamp = (timestamp) => {
    try {
      return format(parseISO(timestamp), 'dd MMM yyyy, hh:mm a');
    } catch {
      return timestamp;
    }
  };

  const getEntityTypeBadge = (type) => {
    if (type === 'delegation_task') {
      return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200"><FileText className="w-3 h-3 mr-1" />Delegation</Badge>;
    }
    if (type === 'checklist_task') {
      return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><CheckSquare className="w-3 h-3 mr-1" />Checklist</Badge>;
    }
    if (type === 'bom') {
      return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200"><Boxes className="w-3 h-3 mr-1" />BOM</Badge>;
    }
    if (type === 'work_order') {
      return <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200"><Workflow className="w-3 h-3 mr-1" />Work Order</Badge>;
    }
    if (type === 'production_plan') {
      return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200"><Wrench className="w-3 h-3 mr-1" />Plan</Badge>;
    }
    if (type === 'purchase_order') {
      return <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200"><FileText className="w-3 h-3 mr-1" />PO</Badge>;
    }
    if (type === 'project') {
      return <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200"><FileText className="w-3 h-3 mr-1" />Project</Badge>;
    }
    if (type === 'employee_request') {
      return <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200"><FileText className="w-3 h-3 mr-1" />Emp Req</Badge>;
    }
    return <Badge variant="outline">{type}</Badge>;
  };

  const getActionBadge = (action) => {
    const colors = {
      update: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      complete: 'bg-green-50 text-green-700 border-green-200',
      undo: 'bg-orange-50 text-orange-700 border-orange-200',
      delete: 'bg-red-50 text-red-700 border-red-200',
      // Phase 1 BOM auto-propagation actions (services/audit_logging.py shape)
      edit: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      bom_propagation: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      cancelled: 'bg-orange-50 text-orange-700 border-orange-200',
      deleted: 'bg-red-50 text-red-700 border-red-200',
      delete_rejected: 'bg-red-50 text-red-700 border-red-200',
      weekly_closure: 'bg-blue-50 text-blue-700 border-blue-200',
    };
    return <Badge variant="outline" className={colors[action] || ''}>{action}</Badge>;
  };

  // Diff renderer — handles all three audit-log conventions in this codebase:
  //   Legacy delegation/checklist : `changes` field with { field: { old, new } } pairs
  //   PO/WO cascade writers       : `summary`  field with arbitrary dict
  //   Phase 1 helper rows         : `details`  field with arbitrary dict
  // Both arbitrary-dict shapes flatten the same way; the {old,new} shape is
  // detected and rendered as a colored before→after pill.
  const renderChanges = (raw) => {
    if (!raw || (typeof raw === 'object' && Object.keys(raw).length === 0)) return '-';

    return (
      <div className="space-y-1 max-w-md">
        {Object.entries(raw).map(([field, value]) => {
          // Old { old, new } pair
          if (
            value && typeof value === 'object' && !Array.isArray(value) &&
            ('old' in value || 'new' in value) &&
            Object.keys(value).every((k) => k === 'old' || k === 'new')
          ) {
            return (
              <div key={field} className="text-xs">
                <span className="font-medium text-gray-600">{field}:</span>
                <div className="flex items-center gap-1 mt-0.5">
                  <span className="text-red-600 bg-red-50 px-1 rounded">{String(value.old ?? 'null')}</span>
                  <ArrowRight className="w-3 h-3 text-gray-400" />
                  <span className="text-green-600 bg-green-50 px-1 rounded">{String(value.new ?? 'null')}</span>
                </div>
              </div>
            );
          }
          // Phase 1 free-form detail
          let display;
          if (Array.isArray(value)) {
            display = value.length === 0 ? '[]' : `${value.length} item(s)`;
          } else if (value && typeof value === 'object') {
            display = JSON.stringify(value);
          } else {
            display = String(value ?? '');
          }
          return (
            <div key={field} className="text-xs">
              <span className="font-medium text-gray-600">{field}:</span>{' '}
              <span className="text-gray-800 break-all">{display}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <History className="w-6 h-6" />
              Audit Logs
            </h1>
            <p className="text-muted-foreground">Track changes across Delegation, Checklist, BOM edits, and Work Order propagation events</p>
          </div>
          <Button onClick={fetchLogs} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        {summary && summary.total_changes > 0 && (
          <div className="grid grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Changes (30 days)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary.total_changes}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Delegation Tasks</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{summary.by_entity_type?.delegation_task || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Checklist Tasks</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{summary.by_entity_type?.checklist_task || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Top Editor</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-medium">
                  {summary.by_user?.[0]?.user_name || '-'}
                </div>
                <div className="text-sm text-muted-foreground">
                  {summary.by_user?.[0]?.count || 0} changes
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 items-end">
              <div className="w-48">
                <Label>Task Type</Label>
                <Select
                  value={filters.entity_type || "all"}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, entity_type: value === "all" ? "" : value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="delegation_task">Delegation Tasks</SelectItem>
                    <SelectItem value="checklist_task">Checklist Tasks</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-40">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div className="w-40">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
              <div className="w-24">
                <Label>Limit</Label>
                <Select
                  value={filters.limit.toString()}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, limit: parseInt(value) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="200">200</SelectItem>
                    <SelectItem value="500">500</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={fetchLogs}>Apply Filters</Button>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card>
          <CardHeader>
            <CardTitle>Change History</CardTitle>
            <CardDescription>
              Showing {logs.length} records
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-40">Timestamp</TableHead>
                    <TableHead className="w-28">Type</TableHead>
                    <TableHead className="w-20">Action</TableHead>
                    <TableHead className="w-48">Task</TableHead>
                    <TableHead>Changes</TableHead>
                    <TableHead className="w-36">Changed By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No audit logs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="text-sm">
                          {formatTimestamp(log.timestamp)}
                        </TableCell>
                        <TableCell>
                          {getEntityTypeBadge(log.entity_type)}
                        </TableCell>
                        <TableCell>
                          {getActionBadge(log.action)}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-sm">{log.entity_name}</div>
                          <div className="text-xs text-muted-foreground truncate max-w-[180px]">
                            {log.entity_id}
                          </div>
                        </TableCell>
                        <TableCell>
                          {renderChanges(log.changes ?? log.details ?? log.summary)}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-sm">
                            {log.changed_by_name ?? log.actor_name ?? log.user_name ?? '-'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {log.changed_by_email ?? log.actor_id ?? log.user_id ?? ''}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AuditLogsPage;
